import { useState } from "react";
import { Button } from "../../../../components/Button";
import { Modal } from "../../../../components/Modal";
import { DateField } from "../../../../components/InputField/DateField";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  PaginationState,
} from "@tanstack/react-table";
import { InfoIcon } from "../../../../assets/svgs/InfoIcon";
import ReactPaginate from "react-paginate";
import { DateCell, EmptyStateNoFiles } from "../../../../components/Common";
import { WalletTransactionData } from "../../../../sharedTypes/wallet-types";
import { PAGE_SIZE } from "../../../../constants";
import { getStatusStyles } from "../../../../utils";

interface TransactionProps {
  data: WalletTransactionData[];
  total: number;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  pagination: PaginationState;
}

export const TransactionTable: React.FC<TransactionProps> = (props) => {
  const { setPagination, pagination, data, total } = props;

  const columnHelper = createColumnHelper<WalletTransactionData>();

  const columns = [
    columnHelper.accessor("requestDate", {
      header: () => <p className="tw-whitespace-nowrap tw-text-left">Date</p>,
      cell: (info) => <DateCell value={info.getValue()} />,
    }),

    columnHelper.accessor("narration", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Narration</p>
      ),
      cell: (info) => (
        <p className="tw-h-[5em] tw-flex tw-items-center tw-text-left">
          {info.getValue()}
        </p>
      ),
    }),

    columnHelper.accessor("responseCode", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Response Code</p>
      ),
      cell: (info) => (
        <p className="tw-h-[5em] tw-flex tw-items-center tw-text-left">
          {info.getValue()}
        </p>
      ),
    }),

    columnHelper.accessor("responseDescription", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">
          Response Description
        </p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">
            {info.getValue()?.substring(0, 35)}...
          </p>
          <span className="tw-w-fit tw-max-w-[300px] tw-max-h-[150px] tw-overflow-y-scroll  tw-absolute tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md">
            <p className="tw-max-w-full tw-whitespace-pre-line tw-break-words">
              {info.getValue()}
            </p>
          </span>
        </div>
      ),
    }),

    columnHelper.accessor("walletId", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Wallet ID</p>
      ),
      cell: (info) => (
        <div className="tw-group tw-relative tw-text-left tw-cursor-pointer">
          <p className="tw-whitespace-nowrap">{info.getValue()}</p>
        </div>
      ),
    }),

    columnHelper.accessor("reference", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Reference ID</p>
      ),
      cell: (info) => (
        <p className="tw-whitespace-nowrap tw-text-left">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("amount", {
      header: () => <p className="tw-whitespace-nowrap tw-text-left">Amount</p>,
      cell: (info) => (
        <p className="tw-whitespace-nowrap tw-text-left">₦ {info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("fee", {
      header: () => (
        <span className="tw-group tw-relative tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
          <p className="tw-whitespace-nowrap tw-text-left">Fee</p>
          <InfoIcon className="tw-w-4 tw-h-4" />
          <span className="tw-w-fit tw-max-w-[300px]  tw-absolute tw-top-[1.5em] tw-hidden group-hover:tw-flex tw-bg-neutral-100 tw-border-[.5px] tw-border-neutral-500 tw-p-[.5em] tw-pr-[1em] tw-rounded-[.5em] tw-z-10 tw-shadow-md tw-whitespace-nowrap">
            More info on fee
          </span>
        </span>
      ),
      cell: (info) => <p>₦ {info.renderValue()}</p>,
    }),

    columnHelper.accessor("transactionDirection", {
      header: () => (
        <p className="tw-whitespace-nowrap tw-text-left">Transaction Type</p>
      ),
      cell: (info) => (
        <p
          className={`tw-px-[1em] tw-text-[.875em] tw-text-left tw-w-fit tw-py-[.4em] tw-border-[.5px] tw-rounded-full ${getStatusStyles(
            info.getValue()
          )}`}
        >
          {info.getValue()}
        </p>
      ),
    }),
  ];

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const startItem = (pagination.pageIndex - 1) * pagination.pageSize + 1;
  const endItem = Math.min(
    pagination.pageIndex * pagination.pageSize,
    total as number
  );

  const handlePageClick = (event: { selected: number }) => {
    setPagination({ ...pagination, pageIndex: event.selected + 1 });
  };

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-overflow-x-auto">
      {data?.length > 0 ? (
        <>
          <table className="tw-min-w-full tw-h[20em] tw-table-auto tw-bg-white">
            <thead className="tw-bg-neutral-200">
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="tw-py-[1em] tw-px-[1em] tw-text-[.875em] tw-text-neutral-700"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableInstance.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="tw-text-neutral-800 !tw-text-[.75em]  tw-font-[300] tw-px-[1em]"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="tw-w-full tw-flex tw-justify-end tw-mt-[2em]">
            <div className="tw-w-fit tw-flex tw-items-center tw-gap-[1em]">
              <p className="tw-text-[.75em] tw-font-[600] tw-text-neutral-900">
                {startItem} - {endItem} of {total}
              </p>
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                initialPage={pagination.pageIndex - 1}
                pageCount={Math.ceil(total / PAGE_SIZE)}
                previousLabel="<"
                renderOnZeroPageCount={null}
                containerClassName={"pagination"}
              />
            </div>
          </div>
        </>
      ) : (
        <EmptyStateNoFiles text="No transactions to report yet" />
      )}
    </div>
  );
};
